/**
 * Area `live-activity` of the console gateway — the account-wide Live activity page (2026-09-29).
 *
 * The sidebar's "Live activity" row used to open the FIRST VoiceAgent's Live tab, copied from the
 * prototype, so a conversation on any other agent never showed there: during plan-007's live checks
 * the product owner called one agent and watched the page stay empty. The row now opens `/live`,
 * which lists every conversation in flight on every VoiceAgent the person may see
 * (`GET /v1/conversations/live-activity`), each naming its agent and linking to that agent's own
 * Live tab — where the channel-unit meter and the day's figures still live, because both belong to
 * one agent.
 *
 * The register is EMPTY: the operation is served.
 *
 * AMENDED 2026-10-19 (plan-021 WAPP-HIST, design §33.13.3, OD-16): each row carries its `acting_stint` — the VoiceAgent
 * answering it now, which is the entry VoiceAgent until the conversation is passed; `voiceAgentId` and `voiceAgentName`
 * stay the entry's (whose Live tab the panel links to).
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type { ChannelLabel, ConsoleGateway, LiveConversationView } from "../gateway.js";
import { readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import {
  elapsedSecondsOf,
  phaseOf,
  readChannelLabel,
  readMedium,
  readNullableText,
  readOutboundOrigin,
} from "../conversation-view.js";
import { actingStintOfWire } from "../voice-agent-stint-view.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

/** One conversation in flight, with the VoiceAgent and workspace it is on and how it came in. */
export interface LiveActivityConversationView extends LiveConversationView {
  readonly voiceAgentId: string;
  readonly voiceAgentName: string;
  /**
   * WAPP-ENDCALL: the workspace the conversation is in. "End call" names the conversation in the OPEN workspace (the
   * route reads it from the principal), so the page offers it only on that workspace's rows.
   */
  readonly workspaceId: string;
  readonly workspaceName: string;
  readonly isTest: boolean;
  readonly channelLabel: ChannelLabel;
  /** Tells website visitors apart ("Visitor 8F22A1"); `null` for any other channel label. */
  readonly visitorTag: string | null;
}

export interface LiveActivityView {
  readonly liveConversationCount: number;
  /** Oldest first, as the API answers. */
  readonly conversation: readonly LiveActivityConversationView[];
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** Every conversation in flight on every VoiceAgent this person may see (History's visibility). */
    getLiveActivity(): Promise<LiveActivityView>;
  }
}

export const liveActivityGap: readonly GatewayGap[] = [];

export function liveActivityOf(body: unknown, now: Date): LiveActivityView {
  const data = readRecord(readRecord(body, "listLiveActivity")["data"], "listLiveActivity.data");
  const conversation = readArray(data["conversation"], "listLiveActivity.data.conversation").map(
    (row, index): LiveActivityConversationView => {
      const path = `listLiveActivity.data.conversation[${String(index)}]`;
      const one = readRecord(row, path);
      return {
        conversationId: readText(one["conversation_id"], `${path}.conversation_id`),
        partyAddress: readNullableText(one["party_address"], `${path}.party_address`),
        medium: readMedium(one["medium"], `${path}.medium`),
        languageCode: readNullableText(one["language_code"], `${path}.language_code`),
        phase: phaseOf(readNullableText(one["last_event_kind"], `${path}.last_event_kind`)),
        elapsedSeconds: elapsedSecondsOf(readText(one["started_at"], `${path}.started_at`), now),
        voiceAgentId: readText(one["voice_agent_id"], `${path}.voice_agent_id`),
        voiceAgentName: readText(one["voice_agent_name"], `${path}.voice_agent_name`),
        workspaceId: readText(one["workspace_id"], `${path}.workspace_id`),
        workspaceName: readText(one["workspace_name"], `${path}.workspace_name`),
        isTest: readBoolean(one["is_test"], `${path}.is_test`),
        channelLabel: readChannelLabel(one["channel_label"], `${path}.channel_label`),
        visitorTag: readNullableText(one["visitor_tag"], `${path}.visitor_tag`),
        // plan-021 (design §33.13.3, D-17): the VoiceAgent answering it now — the page's "Agent now".
        actingStint: actingStintOfWire(one["acting_stint"], `${path}.acting_stint`),
        // WAPP-ENDCALL: an outbound conversation's origin — where "End call" is offered.
        outboundOrigin: readOutboundOrigin(one, `${path}.outbound_origin`),
      };
    },
  );
  return {
    liveConversationCount: readWholeNumber(
      data["live_conversation_count"],
      "listLiveActivity.data.live_conversation_count",
    ),
    conversation,
  };
}

export function liveActivityApiSlice(
  client: WeTalkApiClient,
  context: SliceContext,
): Pick<ConsoleGateway, "getLiveActivity"> {
  return {
    async getLiveActivity(): Promise<LiveActivityView> {
      return liveActivityOf(assertOk(await client.listLiveActivity()), context.now());
    },
  };
}
